
// src/components/Receipt.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import "./invoice-form.css"; // re-uses existing form styles

const API_BASE = process.env.REACT_APP_API_BASE || "https://dairy.server.statify.co.ke";


function emptyLine() {
  return { product_id: null, product_name: "", qty: 1, unit_cost: 0, total_amount: 0 };
}

function Section({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  return (
    <div className="section">
      <div
        role="button"
        tabIndex={0}
        className={`section-header ${open ? "open" : ""}`}
        onClick={() => setOpen(v => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(v => !v); } }}
        aria-expanded={open}
      >
        <div className="section-title">{title}</div>
        <div className={`section-chevron ${open ? "open" : ""}`} aria-hidden="true">▾</div>
      </div>
      <div className={`section-body ${open ? "open" : ""}`} aria-hidden={!open}>
        <div className="section-body-inner">{children}</div>
      </div>
    </div>
  );
}

function ReceiptFormContent() {
  const { id } = useParams(); // optional edit route
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]); // sales invoices
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInvoicesList, setLoadingInvoicesList] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    receipt_number: "", // server-generated after create
    receipt_date: new Date().toISOString(), // auto
    customer_id: null,
    transaction_type: "cash", // 'cash' | 'credit'
    invoice_id: null, // only applicable if transaction_type === 'credit'
    mode_of_payment: "",
    payment_reference: "",
    description: "",
    lines: [emptyLine()],
    total_amount: 0
  });

  useEffect(() => {
    loadReferenceData();
    loadInvoices();
  }, []); // eslint-disable-line

  useEffect(() => {
    // if editing via route, load receipt and populate form (optional)
    if (!isEdit) return;
    setLoading(true);
    fetch(`${API_BASE}/api/receipts/${id}/`)
      .then(r => { if (!r.ok) throw new Error("Failed to load receipt"); return r.json(); })
      .then(data => {
        setForm({
          receipt_number: data.receipt_number ?? "",
          receipt_date: data.date ?? new Date().toISOString(),
          customer_id: data.customer_id ?? null,
          transaction_type: data.transaction_type ?? "cash",
          invoice_id: data.invoice_id ?? null,
          mode_of_payment: data.mode_of_payment ?? "",
          payment_reference: data.payment_reference ?? "",
          description: data.description ?? "",
          lines: Array.isArray(data.lines) && data.lines.length ? data.lines.map(l => ({
            product_id: l.product_id ?? null,
            product_name: l.product_name ?? "",
            qty: Number(l.quantity ?? 1),
            unit_cost: Number(l.unit_cost ?? 0),
            total_amount: Number(l.amount ?? (Number(l.quantity || 0) * Number(l.unit_cost || 0)))
          })) : [emptyLine()],
          total_amount: Number(data.total_amount ?? 0)
        });
      })
      .catch(err => { console.error(err); alert("Failed to load receipt: " + (err.message || err)); })
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  async function loadReferenceData() {
    fetch(`${API_BASE}/api/customers/`).then(r => r.ok ? r.json() : []).then(setCustomers).catch(()=>{});
    fetch(`${API_BASE}/api/products/`).then(r => r.ok ? r.json() : []).then(setProducts).catch(()=>{});
  }

  async function loadInvoices() {
    setLoadingInvoicesList(true);
    try {
      const res = await fetch(`${API_BASE}/api/sales/`);
      if (!res.ok) {
        setInvoices([]);
        return;
      }
      const list = await res.json();
      setInvoices(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed loading invoices", err);
      setInvoices([]);
    } finally {
      setLoadingInvoicesList(false);
    }
  }

  function setField(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function updateLine(idx, patch) {
    setForm(prev => {
      const newLines = prev.lines.map((ln, i) => i === idx ? { ...ln, ...patch } : ln);
      if (patch.qty !== undefined || patch.unit_cost !== undefined) {
        const q = Number(newLines[idx].qty || 0);
        const uc = Number(newLines[idx].unit_cost || 0);
        newLines[idx].total_amount = Number((q * uc).toFixed(2));
      }
      return { ...prev, lines: newLines };
    });
  }

  function addLine(prefill) {
    setForm(prev => ({ ...prev, lines: [...(prev.lines || []), prefill || emptyLine()] }));
  }
  function removeLine(idx) {
    setForm(prev => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) || [emptyLine()] }));
  }

  // When transaction type is credit and user selects an invoice,
  // fetch that invoice and prefill lines (but only if invoice.customer_id === selected customer).
  async function onSelectInvoice(invoiceId) {
    if (!invoiceId) {
      setField("invoice_id", null);
      return;
    }
    // ensure customer is selected first
    if (!form.customer_id) {
      alert("Please select a customer first.");
      return;
    }
    setField("invoice_id", invoiceId);
    try {
      const res = await fetch(`${API_BASE}/api/sales/${invoiceId}/`);
      if (!res.ok) throw new Error("Failed to load selected invoice");
      const inv = await res.json();

      // ensure invoice belongs to selected customer
      const invCustomerId = inv.customer_id ?? inv.customer?.id ?? null;
      if (String(invCustomerId) !== String(form.customer_id)) {
        alert("Selected invoice does not belong to the selected customer. Please pick an invoice for this customer.");
        // clear invoice selection
        setField("invoice_id", null);
        return;
      }

      // map invoice lines into receipt lines
      const mapped = Array.isArray(inv.sale_items) && inv.sale_items.length ? inv.sale_items.map(li => ({
        product_id: li.product_id ?? null,
        product_name: li.product_name ?? (li.product?.name || ""),
        qty: Number(li.quantity ?? 1),
        unit_cost: Number(li.price_per_unit ?? 0),
        total_amount: Number(li.amount ?? (Number(li.quantity || 0) * Number(li.price_per_unit || 0)))
      })) : [emptyLine()];

      // set lines from invoice
      setForm(prev => ({ ...prev, lines: mapped }));
    } catch (err) {
      console.error(err);
      alert("Failed to load invoice lines: " + (err.message || err));
      setField("invoice_id", null);
    }
  }

  // pick product for a line (manual override)
  function pickProduct(idx, pid) {
    const p = products.find(x => String(x.id) === String(pid));
    if (!p) return updateLine(idx, { product_id: null, product_name: "", unit_cost: 0, total_amount: 0 });
    updateLine(idx, { product_id: p.id, product_name: p.name, unit_cost: Number(p.standard_rate || 0) });
  }

  const totalAmount = useMemo(() => {
    const t = (form.lines || []).reduce((s, l) => s + (Number(l.total_amount || 0)), 0);
    return Number(t.toFixed(2));
  }, [form.lines]);

  // Keep total_amount in form in sync (useful for display or saving)
  useEffect(() => {
    setForm(prev => ({ ...prev, total_amount: totalAmount }));
  }, [totalAmount]); // eslint-disable-line

  // Save receipt
  async function handleSave(e) {
    e.preventDefault();

    if (!form.customer_id) return alert("Please select a customer.");
    if (form.transaction_type === "credit" && !form.invoice_id) return alert("Please select an invoice for credit transactions.");

    setSaving(true);
    try {
      const payload = {
        // Do NOT include receipt_number — let server autogenerate
        date: isEdit ? form.receipt_date : new Date().toISOString(),
        customer_id: form.customer_id,
        transaction_type: form.transaction_type,
        invoice_id: form.transaction_type === "credit" ? form.invoice_id : null,
        mode_of_payment: form.mode_of_payment || null,
        payment_reference: form.payment_reference || null,
        description: form.description || null,
        total_amount: totalAmount,
        lines: form.lines.map((l, idx) => ({
          line_no: idx + 1,
          product_id: l.product_id,
          product_name: l.product_name,
          quantity: Number(l.qty || 0),
          unit_cost: Number(l.unit_cost || 0),
          amount: Number(l.total_amount || 0)
        }))
      };

      const url = isEdit ? `${API_BASE}/api/receipts/${id}/` : `${API_BASE}/api/receipts/`;
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        try { const j = JSON.parse(txt || "{}"); throw new Error(j.error || j.message || txt || res.statusText); } catch { throw new Error(txt || res.statusText); }
      }

      // For create: server may return created receipt including receipt_number
      if (!isEdit) {
        try {
          const created = await res.json().catch(() => null);
          if (created) {
            setForm(prev => ({ ...prev, receipt_number: created.receipt_number ?? prev.receipt_number, receipt_date: created.date ?? prev.receipt_date }));
          }
        } catch (err) { /* ignore */ }
      }

      alert("Receipt saved successfully.");
      // Optionally navigate to receipts list or reset form
      navigate("/receipts");
    } catch (err) {
      console.error(err);
      alert("Save failed: " + (err.message || err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="form-card">
      <h2>{isEdit ? "Edit Receipt" : "New Receipt"}</h2>
      <p className="lead">Create a receipt. Date/time is generated automatically.</p>

      <form onSubmit={handleSave} className="form-grid">
        <Section title="Header" defaultOpen={true}>
          <div className="form-grid form-grid-3">
            {/* Receipt ID (readonly when present) */}
            {form.receipt_number && (
              <div className="field">
                <label>Receipt ID</label>
                <div className="muted">{form.receipt_number}</div>
              </div>
            )}

            <div className="field">
              <label>Receipt Date</label>
              <div className="muted">{form.receipt_date ? new Date(form.receipt_date).toLocaleString() : new Date().toLocaleString()}</div>
            </div>

            <div className="field">
              <label>Customer</label>
              <select className="input" value={form.customer_id ?? ""} onChange={e => setField("customer_id", e.target.value || null)}>
                <option value="">-- Select customer --</option>
                {customers.map(c => <option key={c.id} value={c.id}>{`${c.id} — ${c.name}`}</option>)}
              </select>
            </div>

            <div className="field">
              <label>Transaction Type</label>
              <select className="input" value={form.transaction_type} onChange={e => setField("transaction_type", e.target.value)}>
                <option value="cash">Cash</option>
                <option value="credit">Credit</option>
              </select>
            </div>

            {/* Invoice selector appears only for credit */}
            {form.transaction_type === "credit" && (
              <div className="field">
                <label>Invoice No (Credit)</label>
                <select
                  className="input"
                  value={form.invoice_id ?? ""}
                  onChange={e => onSelectInvoice(e.target.value || null)}
                >
                  <option value="">-- Select invoice (for this customer) --</option>
                  {/* filter invoices client-side to show only those matching selected customer, if any */}
                  {invoices
                    .filter(inv => !form.customer_id || String(inv.customer_id ?? inv.customer?.id ?? "") === String(form.customer_id))
                    .map(inv => (
                      <option key={inv.id} value={inv.id}>
                        {inv.invoice_number ? `${inv.invoice_number} — ${inv.id}` : `#${inv.id}`}
                      </option>
                    ))}
                </select>
                <div className="hint">Selecting an invoice will prefill the receipt lines from that invoice.</div>
              </div>
            )}

            <div className="field">
              <label>Mode of payment</label>
              <input className="input" value={form.mode_of_payment} onChange={e => setField("mode_of_payment", e.target.value)} placeholder="e.g. Cash, Mpesa, Bank Transfer" />
            </div>

            <div className="field">
              <label>Payment Reference</label>
              <input className="input" value={form.payment_reference} onChange={e => setField("payment_reference", e.target.value)} placeholder="Transaction reference" />
            </div>

            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Description</label>
              <textarea className="textarea" rows={3} value={form.description} onChange={e => setField("description", e.target.value)} placeholder="Optional description or notes" />
            </div>
          </div>
        </Section>

        <Section title="Lines" defaultOpen={true}>
          <div style={{ marginTop: 8 }}>
            <table className="compact-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>Line No</th>
                  <th style={{ width: 120 }}>Receipt ID</th>
                  <th>Product (ID &amp; Name)</th>
                  <th style={{ width: 120 }}>Quantity</th>
                  <th style={{ width: 140 }}>Unit Cost</th>
                  <th style={{ width: 140 }}>Total Amount</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((ln, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{form.receipt_number || "-"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8, flexDirection: "column" }}>
                        <select className="input" value={ln.product_id ?? ""} onChange={e => pickProduct(i, e.target.value)}>
                          <option value="">-- select product --</option>
                          {products.map(p => <option key={p.id} value={p.id}>{`${p.id} — ${p.name}`}</option>)}
                        </select>
                        {!ln.product_id ? (
                          <input className="input" style={{ marginTop: 8 }} value={ln.product_name} onChange={e => updateLine(i, { product_name: e.target.value })} placeholder="Product name" />
                        ) : (
                          <div className="muted" style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "#fff" }}>
                            {ln.product_name || "-"}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <input className="input" type="number" min="0" step="0.01" value={ln.qty} onChange={e => updateLine(i, { qty: Number(e.target.value) })} />
                    </td>
                    <td>
                      <input className="input" type="number" min="0" step="0.01" value={ln.unit_cost} onChange={e => updateLine(i, { unit_cost: Number(e.target.value) })} />
                    </td>
                    <td>
                      <div className="muted">{(Number(ln.total_amount) || (Number(ln.qty || 0) * Number(ln.unit_cost || 0))).toFixed(2)}</div>
                    </td>
                    <td>
                      <button type="button" className="btn btn-danger small-btn" onClick={() => removeLine(i)} disabled={form.lines.length === 1}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: 12 }}>
              <button type="button" className="add-line" onClick={() => addLine()}>Add line item</button>
            </div>
          </div>
        </Section>

        <div className="form-actions" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <button type="button" className="btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ marginBottom: 8 }}><strong>Total: {totalAmount.toFixed(2)}</strong></div>
            <button type="submit" className="btn btn-success" disabled={saving}>{saving ? "Saving..." : isEdit ? "Update Receipt" : "Create Receipt"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

/**
 * Default export: wrap with DashboardLayout unless caller passes { noLayout: true } prop.
 */
export default function Receipt(props) {
  const { noLayout } = props || {};
  const content = <ReceiptFormContent {...props} />;

  if (noLayout) return content;
  return <DashboardLayout>{content}</DashboardLayout>;
}

